import { Grid } from '@mui/material';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import React from 'react';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from '../../../app/hooks/hooks';
import { getPartnerId, getServicingOpCo } from '../../../app/store/combinedSelectors';
import { updateStageState } from '../../../app/store/EligibilityService/EligibilityServiceSlice';
import { goToClaimsBtn, goToDocumentDamageBtn, skipBtn } from '../../../Constants/Constants';
import { PostMessageCodes } from '../../../Constants/PostMessageCodes';
import { Features, OpCo, PartnerId } from '../../../types/type';
import { getNextView, getStagesInOrder, postMessageToParentApp } from '../../../utils/Utilities';
import CallToAction from '../../shared/CallToAction/CallToAction';
import ModalLinearActivityIndicator from '../../shared/ModalLinearActivityIndicator/ModalLinearActivityIndicator';
import PropertyServiceInfo from '../../shared/PropertyServiceInfo/PropertyServiceInfo';
import StepProgressBar from '../../shared/StepProgressBar/StepProgressBar';

export default function DocumentDamageServicesHome() {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const [modalLoadingOpen, setModalLoadingOpen] = React.useState(false);
  const availableProgressStages = useAppSelector((state) => state.services.availableProgressStages);
  const claimSummary = useAppSelector((state) => state.claimDetail.claimSummary);
  const opco = useSelector(getServicingOpCo);
  const partnerId = useSelector(getPartnerId);

  // Get the rest of stages excluding the current stage. Find the next stage which is valid
  const nextStage = getStagesInOrder(
    availableProgressStages.filter(
      (stage) => stage.id !== Features.DOCUMENT_DAMAGE && !stage.completed && stage.isEligible && !stage.skipped
    )
  )[0];

  function handleNext() {
    navigate('/document-damage-services-selection');
  }

  function handleSkip() {
    //Update the current stage state and prepare the next stage state.
    const updatedStages = availableProgressStages.map((stage) => {
      stage =
        stage.id === Features.DOCUMENT_DAMAGE
          ? {
              ...stage,
              completed: false,
              viewActive: false,
              showProgress: true,
              skipped: true,
            }
          : stage;
      stage =
        stage.id === nextStage.id
          ? {
              ...stage,
              completed: true,
              viewActive: true,
              showProgress: false,
            }
          : stage;
      return stage;
    });
    dispatch(updateStageState({ updatedStages: updatedStages }));
    navigate(getNextView(nextStage.id));
  }

  async function goToClaimsOverview() {
    if (partnerId?.toUpperCase() === PartnerId.AFICS) {
      setModalLoadingOpen(true);
      postMessageToParentApp(
        PostMessageCodes.DIGITAL_CLAIM_POST_FNOL_GO_TO_CLAIMS,
        undefined,
        undefined,
        claimSummary?.policyNumber
      );
    } else if (opco === OpCo.HOMESITE || opco === OpCo.AMFAM) {
      navigate('/claims-overview', {
        state: {
          isClaimRefreshNeeded: true,
        },
      });
    } else {
      navigate('/claim-detail', {
        state: {
          isClaimDetailRefreshNeeded: true,
        },
      });
    }
  }

  const headerContent = 'Document the damages to your property with Plnar Snap';

  return (
    <Box sx={{ width: '100%' }}>
      <StepProgressBar stateInfo={availableProgressStages}></StepProgressBar>
      <PropertyServiceInfo
        headerContent={headerContent}
        infoContent={
          <>
            <Grid item xs={12}>
              <Typography sx={{ marginTop: '20px' }} variant="body1">
                We work with Plnar to document your property’s damages and move your claim forward as quickly as
                possible.
              </Typography>
            </Grid>
            <Grid item xs={12}>
              <Typography sx={{ marginTop: '20px' }} variant="body1">
                Plnar uses photos to measure and create 3D models of interior rooms to help your adjuster estimate
                repair costs.
              </Typography>
            </Grid>
            <Grid item xs={12}>
              <Typography sx={{ marginTop: '20px' }} variant="body1">
                By selecting<b> Go to Document Damage</b>, you will be asked to download Plnar Snap on your mobile
                device.
              </Typography>
            </Grid>
            <Grid sx={{ marginTop: '20px' }} item xs={12}>
              <Typography variant="body1">
                If you don’t have a mobile device your claim adjuster will contact you to document the damage for your
                claim.
              </Typography>
            </Grid>
          </>
        }
      ></PropertyServiceInfo>
      {modalLoadingOpen && <ModalLinearActivityIndicator />}
      <Grid
        container
        sx={{
          width: '100%',
          maxWidth: '1280px',
          minWidth: '310px',
          margin: '0 auto',
          marginTop: '1em',
        }}
      >
        <Grid item xs={12}>
          <CallToAction
            nextButton={true}
            nextButtonContent={goToDocumentDamageBtn}
            handleNext={handleNext}
            backButton={false}
            skipButton={true}
            skipButtonContent={skipBtn}
            handleSkip={handleSkip}
            cancelButton={true}
            cancelButtonContent={goToClaimsBtn}
            handleCancel={goToClaimsOverview}
          ></CallToAction>
        </Grid>
      </Grid>
    </Box>
  );
}
